import {memo, useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {PercentIcon} from "@pg-design/icons-module";
import type {ITab} from "@pg-design/tabs-v2-module";
import {Tabs} from "@pg-design/tabs-v2-module";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, QueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty} from "@pg-mono/nodash";
import {numberFormat} from "@pg-mono/string-utils";

import {useShouldShowPriceByOfferConfigAndUser} from "../../../../hooks/use_should_show_price_by_offer_config_and_user";
import {propertyListSortOptions} from "../../../../property/constants/property_list_sort_options";
import {IPropertyListProperty} from "../../../../property/types/PropertyListProperty";
import {IPropertyListObj} from "../../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../../property_list/PropertyListTabSwitcher";
import {Country} from "../../../../region/types/Country";
import {getLocalStoragePropertyFilters, updateLocalStoragePropertyFilters} from "../../../../search/utils/use_localstorage_property_filters";
import {useAppSelector} from "../../../../store/hooks";
import {IAmplitudeItemListContext} from "../../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {FilterPresets, useUserPreferencesFilters} from "../../../../user/hooks/use_user_preferences_filters";
import {getCurrency} from "../../../../utils/get_currency";
import {getPlanner3dData} from "../../../constants/get_planner_3d_data";
import {getOfferTypeNamePlural} from "../../../helpers/OfferType";
import {hitPropertyListViewChange} from "../../../tracking/hit_property_list_view_change";
import {IOfferDetail} from "../../../types/IOfferDetail";
import {OfferPriceType} from "../../../types/OfferPriceType";
import {fetchOfferDetailLowestPriceProperty} from "../../actions/fetch_offer_detail_lowest_price_property";
import {fetchOfferDetailProperties, PropertyListTabTypes, setOfferDetailPropertiesActiveTab} from "../../actions/fetch_offer_detail_properties";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE} from "../../constants/offer_detail_properties";
import {usePromotionAnchorPropertyListScroll} from "../../hooks/use_promotion_anchor_property_list_scroll";
import {Planner3dModal} from "../../planner3D/components/Planner3D";
import {getOfferDetailPropertyListQueryFilters} from "../../utils/getOfferDetailPropertyListQueryFilters";
import {OfferDetailPromotionPropertyBox} from "./OfferDetailPromotionPropertyBox";
import {OfferDetailPropertyListFilters} from "./OfferDetailPropertyListFilters";
import {OfferDetailPropertyListTable, propertyListTableFiltersFields} from "./OfferDetailPropertyListTable";
import {OfferDetailPropertyListTiles} from "./OfferDetailPropertyListTiles";

interface IProps {
    offerId: number;
    offer: IOfferDetail;
    propertyListObj: IPropertyListObj;
    onFilterValuesChange: (values: ISearchFiltersFormValues) => void;
    propertyListType?: PropertyListType;
    /**
     * When the offer has promotions, enables the promotion experience on the offer detail page: preloads promotion
     * properties, shows the "Wszystkie oferty / Promocyjne" tabulator and renders the promotion-specific card.
     */
    trackingMeta?: {
        listId: string;
        listName: string;
    };
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
}

export interface IMainImage {
    m_img_224x147?: string;
    m_img_375x211?: string;
    m_img_760x428?: string;
    m_img_1500?: string;
    m_img_155x87?: string;
}

export const initialPropertiesFilterValues: ISearchFiltersFormValues = {
    area: {lower: "", upper: ""},
    price: {lower: "", upper: ""},
    rooms: {lower: "", upper: ""},
    floor_choices: [],
    house_storeys: []
};

export const OfferDetailPropertyList = memo((props: IProps) => {
    const {offerId, offer, propertyListObj, onFilterValuesChange, propertyListType, trackingMeta, amplitudeItemListContext} = props;

    const propertiesData = useAppSelector((store) => store.offerDetail.properties);
    const activeTab = useAppSelector((store) => store.offerDetail.properties.activeTab);
    const currentProperties = propertiesData[activeTab];
    const count = currentProperties.count;
    const promotionCount = propertiesData.promotion.count;
    /*
     * Show the "Promocyjne" toggle whenever the offer has promotional apartments (property.is_promo). The count is
     * preloaded on mount directly from the is_promo endpoint, so it is reliable regardless of offer-level promotions
     * or on which page of the regular list the promotional apartments happen to be.
     */
    const showPromotionTab = promotionCount > 0;

    const [currentPropertyListType, setCurrentPropertyListType] = useState(propertyListType ?? PropertyListType.TABLE);
    const isApartment = offer?.is_holiday_location || offer?.is_condohotel;
    const [filterValues, setFilterValues] = useState(initialPropertiesFilterValues);

    const hasPlanner3D = Boolean(getPlanner3dData(offer.id));
    const [showPropertyListBlend, setShowPropertyListBlend] = useState(hasPlanner3D);

    const dispatch = useDispatch();

    const userPreferencesFilters = useUserPreferencesFilters(FilterPresets.WithoutRegion);
    /*
     * filter values for property list - either based on user preferences or temporary filters from local storage
     */
    const localStoragePropertyFilters = getLocalStoragePropertyFilters();
    const initialQueryValues: QueryValues = isEmpty(localStoragePropertyFilters) ? userPreferencesFilters : localStoragePropertyFilters;
    const initialPropertyListQueryFilters = getOfferDetailPropertyListQueryFilters(initialQueryValues, offer.type);

    /*
        Initial clientside data fetch
    */
    useEffect(() => {
        const searchFilters = fromQueryValues<ISearchFiltersFormValues>(propertyListTableFiltersFields, initialPropertyListQueryFilters);

        setFilterValues(searchFilters);

        if (offerId) {
            // fetch filtered properties only when initial filters has been set
            if (!isEmpty(initialQueryValues)) {
                const defaultQueryValues = {page: 1};
                const propertyListQuery = {...initialPropertyListQueryFilters, type: offer.type};

                fetchOfferDetailProperties(
                    offerId,
                    PropertyListTabTypes.FILTER,
                    OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                    defaultQueryValues,
                    propertyListQuery
                )(dispatch);

                dispatch(fetchOfferDetailLowestPriceProperty(offerId, propertyListQuery));
            }

            // preload first page of promotion (is_promo) properties so we know whether the offer has promotional
            // apartments and can render the promotion tab content; this never switches the active tab (see
            // activeTabReducer). The promotional apartments are flagged per-property (property.is_promo) and are
            // independent of offer-level promotions, so the preload always runs.
            fetchOfferDetailProperties(
                offerId,
                PropertyListTabTypes.PROMOTION,
                OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                {page: 1},
                {...initialPropertyListQueryFilters, type: offer.type}
            )(dispatch);

            // clear ls stored filter after fetching, we don't want to reuse it
            updateLocalStoragePropertyFilters({});
        }
    }, []);

    useEffect(() => {
        onFilterValuesChange(filterValues);
    }, [filterValues]);

    // Reset filters when switching to INVESTMENT tab (investment properties with ROI don't support filtering)
    useEffect(() => {
        if (activeTab === PropertyListTabTypes.INVESTMENT) {
            setFilterValues(initialPropertiesFilterValues);
        }
    }, [activeTab]);

    const getOfferTypeName = () => {
        const offerTypeName = getOfferTypeNamePlural(offer.type);
        return offerTypeName.charAt(0).toUpperCase() + offerTypeName.slice(1);
    };

    const changeListType = (type: PropertyListType) => {
        hitPropertyListViewChange(type === PropertyListType.TABLE ? "lista" : "rzuty");
        setCurrentPropertyListType(type);
    };

    /*
        Tabulator handling: switching keeps the current filters and sort, but always resets to the first page.
        The promotion tab automatically appends `is_promo: true` (see fetchOfferDetailProperties).
    */
    const promotionTabs = useMemo<ITab<PropertyListTabTypes>[]>(
        () => [
            {title: "Wszystkie oferty", value: PropertyListTabTypes.FILTER},
            {
                title: (
                    <span className={promotionTabTitleStyle}>
                        <PercentIcon size="1.4" wrapperSize="2.4" wrapperColor="var(--colors-highlight)" />
                        <span>Promocyjne ({promotionCount})</span>
                    </span>
                ),
                value: PropertyListTabTypes.PROMOTION
            }
        ],
        [promotionCount]
    );
    const tabsValue = activeTab === PropertyListTabTypes.PROMOTION ? PropertyListTabTypes.PROMOTION : PropertyListTabTypes.FILTER;

    const setActivePropertyTab = useCallback(
        (tab: PropertyListTabTypes) => {
            if (tab !== activeTab) {
                dispatch(setOfferDetailPropertiesActiveTab(tab));
            }

            if (tab !== propertyListObj.tab.currentTab) {
                propertyListObj.tab.setCurrentTab(tab);
            }

            if (propertyListObj.page.currentPage !== 1) {
                propertyListObj.page.setCurrentPage(1);
            }
        },
        [activeTab, dispatch, propertyListObj.page, propertyListObj.tab]
    );

    const fetchPropertyTab = (tab: PropertyListTabTypes) => {
        const filterQueryValues = toQueryValues(propertyListTableFiltersFields, filterValues);

        fetchOfferDetailProperties(
            offerId,
            tab,
            OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
            {page: 1, sort: propertyListObj.sort.currentSort},
            filterQueryValues
        )(dispatch);
    };

    const handlePropertyTabChange = (tab: PropertyListTabTypes) => {
        if (tab === activeTab && tab === propertyListObj.tab.currentTab) {
            return;
        }

        setActivePropertyTab(tab);
        fetchPropertyTab(tab);
    };

    usePromotionAnchorPropertyListScroll({
        activeTab,
        promotionRequestState: propertiesData.promotion.requestState,
        propertyListCurrentTab: propertyListObj.tab.currentTab,
        setActivePropertyTab,
        showPromotionTab
    });

    const renderPromotionTile =
        activeTab === PropertyListTabTypes.PROMOTION ? (property: IPropertyListProperty) => <OfferDetailPromotionPropertyBox property={property} /> : undefined;

    const offerRangePrice = (offer.region.country === Country.SPAIN && offer?.stats?.ranges_price_min) || offer.stats?.ranges_price_m2_min || null;

    const priceType = offer.region.country === Country.SPAIN && offer.price_type === OfferPriceType.NETTO ? " netto" : "";

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const propertyListHeadline = `${isApartment ? "Apartamenty" : getOfferTypeName()} w inwestycji`;
    const propertyListName = trackingMeta?.listName ?? propertyListHeadline;
    const propertyListId = trackingMeta?.listId ?? `${isApartment ? "apartamenty" : getOfferTypeName().toLowerCase()}_w_inwestycji`;
    return (
        <>
            <section
                className={cx(
                    propertyListWrapperStyle,
                    showPropertyListBlend && propertyListObj.properties?.length > 0 ? propertyListBlendStyle : propertyListExpandedStyle
                )}
            >
                {offer && (
                    <>
                        <Text variant="headline_4" as="h2" className={headlineStyle}>
                            {propertyListHeadline}
                            {` (${count})`}
                        </Text>

                        {shouldShowPrice && (offer.region.country === Country.SPAIN || offer.region.country === Country.POLAND) && offerRangePrice && (
                            <Text variant="headline_5" as="h3" className={priceHeadlineStyle}>
                                od{" "}
                                <span className="bt">
                                    <Highlight>
                                        <Text variant="headline_2" as="span">
                                            {`${numberFormat(offerRangePrice)}${priceType} ${getCurrency({
                                                currency: offer.currency
                                            })}`}
                                        </Text>

                                        {offer.region.country === Country.SPAIN && offer.stats?.ranges_price_min ? null : (
                                            <>
                                                /m<sup>2</sup>
                                            </>
                                        )}
                                    </Highlight>
                                </span>
                            </Text>
                        )}
                    </>
                )}

                <OfferDetailPropertyListFilters
                    propertyListObj={propertyListObj}
                    filterValues={filterValues}
                    offerId={offerId}
                    offer={offer}
                    setFilterValues={setFilterValues}
                    sortOptions={propertyListSortOptions}
                    currentPropertyListType={currentPropertyListType}
                    setCurrentPropertyListType={changeListType}
                    tabsSlot={
                        showPromotionTab ? (
                            <Tabs fullWidth variant="contained" size="default" value={tabsValue} onChange={handlePropertyTabChange} tabs={promotionTabs} />
                        ) : undefined
                    }
                />

                <div className={currentPropertyListType === PropertyListType.TILES ? showListStyle : hideListStyle}>
                    <OfferDetailPropertyListTiles
                        filterValues={filterValues}
                        propertyListObj={propertyListObj}
                        offer={offer}
                        renderPropertyTile={renderPromotionTile}
                        amplitudeItemListContext={amplitudeItemListContext}
                        trackingMeta={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                </div>

                <div className={currentPropertyListType === PropertyListType.TABLE ? showListStyle : hideListStyle}>
                    <OfferDetailPropertyListTable
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                        propertyListObj={propertyListObj}
                        offerId={offerId}
                        offer={offer}
                        amplitudeItemListContext={amplitudeItemListContext}
                        trackingMeta={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                </div>
            </section>

            {hasPlanner3D && (
                <div className={buttonsWrapperStyle}>
                    <Planner3dModal offer={offer} vendorId={offer.vendor.id} className={planner3dButtonStyle} routedModalId="detail">
                        <Button variant="filled_secondary">Plan pięter</Button>
                    </Planner3dModal>

                    <Button
                        variant="filled_secondary"
                        onClick={() => setShowPropertyListBlend(false)}
                        className={showPropertyListBlend ? showMoreButtonStyle : hideListStyle}
                    >
                        Pokaż więcej rzutów
                    </Button>
                </div>
            )}
        </>
    );
});

const propertyListWrapperStyle = css({
    position: "relative",
    zIndex: 1,
    transition: "max-height 0.4s ease-in-out",
    mb: 3,
    md: {
        mb: 3
    }
});

const propertyListExpandedStyle = css({
    maxHeight: "8000px",
    md: {
        maxHeight: "3000px"
    }
});

const propertyListBlendStyle = css({
    maxHeight: "1300px",
    overflowY: "hidden",
    "&:after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        height: "10rem",
        background: "linear-gradient(rgba(255, 255, 255, 0), var(--colors-gray-100))"
    },
    md: {
        maxHeight: "744px"
    }
});

const showMoreButtonStyle = css({
    display: "block"
});

const headlineStyle = css({
    pb: 1
});

const priceHeadlineStyle = css({
    pb: 2
});

const promotionTabTitleStyle = css({
    display: "inline-flex",
    alignItems: "center",
    gap: 1
});

const showListStyle = css({
    display: "block"
});

const hideListStyle = css({
    display: "none"
});

const buttonsWrapperStyle = css({
    display: "flex",
    gap: "2rem",
    justifyContent: "center"
});

const planner3dButtonStyle = css({
    display: "flex",
    justifyContent: "center",
    mb: 3,
    md: {
        mb: 3
    }
});
import {memo, useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {PercentIcon} from "@pg-design/icons-module";
import type {ITab} from "@pg-design/tabs-v2-module";
import {Tabs} from "@pg-design/tabs-v2-module";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, QueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty} from "@pg-mono/nodash";
import {numberFormat} from "@pg-mono/string-utils";

import {useShouldShowPriceByOfferConfigAndUser} from "../../../../hooks/use_should_show_price_by_offer_config_and_user";
import {propertyListSortOptions} from "../../../../property/constants/property_list_sort_options";
import {IPropertyListProperty} from "../../../../property/types/PropertyListProperty";
import {IPropertyListObj} from "../../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../../property_list/PropertyListTabSwitcher";
import {Country} from "../../../../region/types/Country";
import {getLocalStoragePropertyFilters, updateLocalStoragePropertyFilters} from "../../../../search/utils/use_localstorage_property_filters";
import {useAppSelector} from "../../../../store/hooks";
import {IAmplitudeItemListContext} from "../../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {FilterPresets, useUserPreferencesFilters} from "../../../../user/hooks/use_user_preferences_filters";
import {getCurrency} from "../../../../utils/get_currency";
import {getPlanner3dData} from "../../../constants/get_planner_3d_data";
import {getOfferTypeNamePlural} from "../../../helpers/OfferType";
import {hitPropertyListViewChange} from "../../../tracking/hit_property_list_view_change";
import {IOfferDetail} from "../../../types/IOfferDetail";
import {OfferPriceType} from "../../../types/OfferPriceType";
import {fetchOfferDetailLowestPriceProperty} from "../../actions/fetch_offer_detail_lowest_price_property";
import {fetchOfferDetailProperties, PropertyListTabTypes, setOfferDetailPropertiesActiveTab} from "../../actions/fetch_offer_detail_properties";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE} from "../../constants/offer_detail_properties";
import {usePromotionAnchorPropertyListScroll} from "../../hooks/use_promotion_anchor_property_list_scroll";
import {Planner3dModal} from "../../planner3D/components/Planner3D";
import {getOfferDetailPropertyListQueryFilters} from "../../utils/getOfferDetailPropertyListQueryFilters";
import {OfferDetailPromotionPropertyBox} from "./OfferDetailPromotionPropertyBox";
import {OfferDetailPropertyListFilters} from "./OfferDetailPropertyListFilters";
import {OfferDetailPropertyListTable, propertyListTableFiltersFields} from "./OfferDetailPropertyListTable";
import {OfferDetailPropertyListTiles} from "./OfferDetailPropertyListTiles";

interface IProps {
    offerId: number;
    offer: IOfferDetail;
    propertyListObj: IPropertyListObj;
    onFilterValuesChange: (values: ISearchFiltersFormValues) => void;
    propertyListType?: PropertyListType;
    /**
     * When the offer has promotions, enables the promotion experience on the offer detail page: preloads promotion
     * properties, shows the "Wszystkie oferty / Promocyjne" tabulator and renders the promotion-specific card.
     */
    trackingMeta?: {
        listId: string;
        listName: string;
    };
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
}

export interface IMainImage {
    m_img_224x147?: string;
    m_img_375x211?: string;
    m_img_760x428?: string;
    m_img_1500?: string;
    m_img_155x87?: string;
}

export const initialPropertiesFilterValues: ISearchFiltersFormValues = {
    area: {lower: "", upper: ""},
    price: {lower: "", upper: ""},
    rooms: {lower: "", upper: ""},
    floor_choices: [],
    house_storeys: []
};

export const OfferDetailPropertyList = memo((props: IProps) => {
    const {offerId, offer, propertyListObj, onFilterValuesChange, propertyListType, trackingMeta, amplitudeItemListContext} = props;

    const propertiesData = useAppSelector((store) => store.offerDetail.properties);
    const activeTab = useAppSelector((store) => store.offerDetail.properties.activeTab);
    const currentProperties = propertiesData[activeTab];
    const count = currentProperties.count;
    const promotionCount = propertiesData.promotion.count;
    /*
     * Show the "Promocyjne" toggle whenever the offer has promotional apartments (property.is_promo). The count is
     * preloaded on mount directly from the is_promo endpoint, so it is reliable regardless of offer-level promotions
     * or on which page of the regular list the promotional apartments happen to be.
     */
    const showPromotionTab = promotionCount > 0;

    const [currentPropertyListType, setCurrentPropertyListType] = useState(propertyListType ?? PropertyListType.TABLE);
    const isApartment = offer?.is_holiday_location || offer?.is_condohotel;
    const [filterValues, setFilterValues] = useState(initialPropertiesFilterValues);

    const hasPlanner3D = Boolean(getPlanner3dData(offer.id));
    const [showPropertyListBlend, setShowPropertyListBlend] = useState(hasPlanner3D);

    const dispatch = useDispatch();

    const userPreferencesFilters = useUserPreferencesFilters(FilterPresets.WithoutRegion);
    /*
     * filter values for property list - either based on user preferences or temporary filters from local storage
     */
    const localStoragePropertyFilters = getLocalStoragePropertyFilters();
    const initialQueryValues: QueryValues = isEmpty(localStoragePropertyFilters) ? userPreferencesFilters : localStoragePropertyFilters;
    const initialPropertyListQueryFilters = getOfferDetailPropertyListQueryFilters(initialQueryValues, offer.type);

    /*
        Initial clientside data fetch
    */
    useEffect(() => {
        const searchFilters = fromQueryValues<ISearchFiltersFormValues>(propertyListTableFiltersFields, initialPropertyListQueryFilters);

        setFilterValues(searchFilters);

        if (offerId) {
            // fetch filtered properties only when initial filters has been set
            if (!isEmpty(initialQueryValues)) {
                const defaultQueryValues = {page: 1};
                const propertyListQuery = {...initialPropertyListQueryFilters, type: offer.type};

                fetchOfferDetailProperties(
                    offerId,
                    PropertyListTabTypes.FILTER,
                    OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                    defaultQueryValues,
                    propertyListQuery
                )(dispatch);

                dispatch(fetchOfferDetailLowestPriceProperty(offerId, propertyListQuery));
            }

            // preload first page of promotion (is_promo) properties so we know whether the offer has promotional
            // apartments and can render the promotion tab content; this never switches the active tab (see
            // activeTabReducer). The promotional apartments are flagged per-property (property.is_promo) and are
            // independent of offer-level promotions, so the preload always runs.
            fetchOfferDetailProperties(
                offerId,
                PropertyListTabTypes.PROMOTION,
                OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                {page: 1},
                {...initialPropertyListQueryFilters, type: offer.type}
            )(dispatch);

            // clear ls stored filter after fetching, we don't want to reuse it
            updateLocalStoragePropertyFilters({});
        }
    }, []);

    useEffect(() => {
        onFilterValuesChange(filterValues);
    }, [filterValues]);

    // Reset filters when switching to INVESTMENT tab (investment properties with ROI don't support filtering)
    useEffect(() => {
        if (activeTab === PropertyListTabTypes.INVESTMENT) {
            setFilterValues(initialPropertiesFilterValues);
        }
    }, [activeTab]);

    const getOfferTypeName = () => {
        const offerTypeName = getOfferTypeNamePlural(offer.type);
        return offerTypeName.charAt(0).toUpperCase() + offerTypeName.slice(1);
    };

    const changeListType = (type: PropertyListType) => {
        hitPropertyListViewChange(type === PropertyListType.TABLE ? "lista" : "rzuty");
        setCurrentPropertyListType(type);
    };

    /*
        Tabulator handling: switching keeps the current filters and sort, but always resets to the first page.
        The promotion tab automatically appends `is_promo: true` (see fetchOfferDetailProperties).
    */
    const promotionTabs = useMemo<ITab<PropertyListTabTypes>[]>(
        () => [
            {title: "Wszystkie oferty", value: PropertyListTabTypes.FILTER},
            {
                title: (
                    <span className={promotionTabTitleStyle}>
                        <PercentIcon size="1.4" wrapperSize="2.4" wrapperColor="var(--colors-highlight)" />
                        <span>Promocyjne ({promotionCount})</span>
                    </span>
                ),
                value: PropertyListTabTypes.PROMOTION
            }
        ],
        [promotionCount]
    );
    const tabsValue = activeTab === PropertyListTabTypes.PROMOTION ? PropertyListTabTypes.PROMOTION : PropertyListTabTypes.FILTER;

    const setActivePropertyTab = useCallback(
        (tab: PropertyListTabTypes) => {
            if (tab !== activeTab) {
                dispatch(setOfferDetailPropertiesActiveTab(tab));
            }

            if (tab !== propertyListObj.tab.currentTab) {
                propertyListObj.tab.setCurrentTab(tab);
            }

            if (propertyListObj.page.currentPage !== 1) {
                propertyListObj.page.setCurrentPage(1);
            }
        },
        [activeTab, dispatch, propertyListObj.page, propertyListObj.tab]
    );

    const fetchPropertyTab = (tab: PropertyListTabTypes) => {
        const filterQueryValues = toQueryValues(propertyListTableFiltersFields, filterValues);

        fetchOfferDetailProperties(
            offerId,
            tab,
            OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
            {page: 1, sort: propertyListObj.sort.currentSort},
            filterQueryValues
        )(dispatch);
    };

    const handlePropertyTabChange = (tab: PropertyListTabTypes) => {
        if (tab === activeTab && tab === propertyListObj.tab.currentTab) {
            return;
        }

        setActivePropertyTab(tab);
        fetchPropertyTab(tab);
    };

    usePromotionAnchorPropertyListScroll({
        activeTab,
        promotionRequestState: propertiesData.promotion.requestState,
        propertyListCurrentTab: propertyListObj.tab.currentTab,
        setActivePropertyTab,
        showPromotionTab
    });

    const renderPromotionTile =
        activeTab === PropertyListTabTypes.PROMOTION ? (property: IPropertyListProperty) => <OfferDetailPromotionPropertyBox property={property} /> : undefined;

    const offerRangePrice = (offer.region.country === Country.SPAIN && offer?.stats?.ranges_price_min) || offer.stats?.ranges_price_m2_min || null;

    const priceType = offer.region.country === Country.SPAIN && offer.price_type === OfferPriceType.NETTO ? " netto" : "";

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const propertyListHeadline = `${isApartment ? "Apartamenty" : getOfferTypeName()} w inwestycji`;
    const propertyListName = trackingMeta?.listName ?? propertyListHeadline;
    const propertyListId = trackingMeta?.listId ?? `${isApartment ? "apartamenty" : getOfferTypeName().toLowerCase()}_w_inwestycji`;
    return (
        <>
            <section
                className={cx(
                    propertyListWrapperStyle,
                    showPropertyListBlend && propertyListObj.properties?.length > 0 ? propertyListBlendStyle : propertyListExpandedStyle
                )}
            >
                {offer && (
                    <>
                        <Text variant="headline_4" as="h2" className={headlineStyle}>
                            {propertyListHeadline}
                            {` (${count})`}
                        </Text>

                        {shouldShowPrice && (offer.region.country === Country.SPAIN || offer.region.country === Country.POLAND) && offerRangePrice && (
                            <Text variant="headline_5" as="h3" className={priceHeadlineStyle}>
                                od{" "}
                                <span className="bt">
                                    <Highlight>
                                        <Text variant="headline_2" as="span">
                                            {`${numberFormat(offerRangePrice)}${priceType} ${getCurrency({
                                                currency: offer.currency
                                            })}`}
                                        </Text>

                                        {offer.region.country === Country.SPAIN && offer.stats?.ranges_price_min ? null : (
                                            <>
                                                /m<sup>2</sup>
                                            </>
                                        )}
                                    </Highlight>
                                </span>
                            </Text>
                        )}
                    </>
                )}

                <OfferDetailPropertyListFilters
                    propertyListObj={propertyListObj}
                    filterValues={filterValues}
                    offerId={offerId}
                    offer={offer}
                    setFilterValues={setFilterValues}
                    sortOptions={propertyListSortOptions}
                    currentPropertyListType={currentPropertyListType}
                    setCurrentPropertyListType={changeListType}
                    tabsSlot={
                        showPromotionTab ? (
                            <Tabs fullWidth variant="contained" size="default" value={tabsValue} onChange={handlePropertyTabChange} tabs={promotionTabs} />
                        ) : undefined
                    }
                />

                <div className={currentPropertyListType === PropertyListType.TILES ? showListStyle : hideListStyle}>
                    <OfferDetailPropertyListTiles
                        filterValues={filterValues}
                        propertyListObj={propertyListObj}
                        offer={offer}
                        renderPropertyTile={renderPromotionTile}
                        amplitudeItemListContext={amplitudeItemListContext}
                        trackingMeta={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                </div>

                <div className={currentPropertyListType === PropertyListType.TABLE ? showListStyle : hideListStyle}>
                    <OfferDetailPropertyListTable
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                        propertyListObj={propertyListObj}
                        offerId={offerId}
                        offer={offer}
                        amplitudeItemListContext={amplitudeItemListContext}
                        trackingMeta={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                </div>
            </section>

            {hasPlanner3D && (
                <div className={buttonsWrapperStyle}>
                    <Planner3dModal offer={offer} vendorId={offer.vendor.id} className={planner3dButtonStyle} routedModalId="detail">
                        <Button variant="filled_secondary">Plan pięter</Button>
                    </Planner3dModal>

                    <Button
                        variant="filled_secondary"
                        onClick={() => setShowPropertyListBlend(false)}
                        className={showPropertyListBlend ? showMoreButtonStyle : hideListStyle}
                    >
                        Pokaż więcej rzutów
                    </Button>
                </div>
            )}
        </>
    );
});

const propertyListWrapperStyle = css({
    position: "relative",
    zIndex: 1,
    transition: "max-height 0.4s ease-in-out",
    mb: 3,
    md: {
        mb: 3
    }
});

const propertyListExpandedStyle = css({
    maxHeight: "8000px",
    md: {
        maxHeight: "3000px"
    }
});

const propertyListBlendStyle = css({
    maxHeight: "1300px",
    overflowY: "hidden",
    "&:after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        height: "10rem",
        background: "linear-gradient(rgba(255, 255, 255, 0), var(--colors-gray-100))"
    },
    md: {
        maxHeight: "744px"
    }
});

const showMoreButtonStyle = css({
    display: "block"
});

const headlineStyle = css({
    pb: 1
});

const priceHeadlineStyle = css({
    pb: 2
});

const promotionTabTitleStyle = css({
    display: "inline-flex",
    alignItems: "center",
    gap: 1
});

const showListStyle = css({
    display: "block"
});

const hideListStyle = css({
    display: "none"
});

const buttonsWrapperStyle = css({
    display: "flex",
    gap: "2rem",
    justifyContent: "center"
});

const planner3dButtonStyle = css({
    display: "flex",
    justifyContent: "center",
    mb: 3,
    md: {
        mb: 3
    }
});
